import type { Conversation } from '../types';

const byRecent = (a: Conversation, b: Conversation) =>
  new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();

export interface PinnedDayGroups {
  pinned: Conversation[];
  today: Conversation[];
  earlier: Conversation[];
}

/**
 * Group conversations into Pinned / Today / Earlier buckets.
 * - Pinned: any conversation with `pinned === true` (server-backed flag).
 * - Today: non-pinned with `updatedAt` >= start of today (local time).
 * - Earlier: everything else.
 *
 * Each bucket is sorted by `updatedAt` descending.
 */
export function groupConversations(
  conversations: Conversation[],
): PinnedDayGroups {
  const pinned: Conversation[] = [];
  const today: Conversation[] = [];
  const earlier: Conversation[] = [];
  const todayStart = startOfToday();

  for (const c of conversations) {
    if (c.pinned) {
      pinned.push(c);
    } else if (new Date(c.updatedAt) >= todayStart) {
      today.push(c);
    } else {
      earlier.push(c);
    }
  }

  pinned.sort(byRecent);
  today.sort(byRecent);
  earlier.sort(byRecent);

  return { pinned, today, earlier };
}

export interface DayGroups {
  today: Conversation[];
  yesterday: Conversation[];
  earlier: Conversation[];
}

/**
 * Group conversations into Today / Yesterday / Earlier buckets, ignoring pinning.
 * Each bucket is sorted by `updatedAt` descending. Suitable for a chronological
 * inline nav listing.
 */
export function groupByDay(conversations: Conversation[]): DayGroups {
  const today: Conversation[] = [];
  const yesterday: Conversation[] = [];
  const earlier: Conversation[] = [];
  const todayStart = startOfToday();
  const yesterdayStart = new Date(todayStart);
  yesterdayStart.setDate(yesterdayStart.getDate() - 1);

  for (const c of conversations) {
    const updated = new Date(c.updatedAt);
    if (updated >= todayStart) {
      today.push(c);
    } else if (updated >= yesterdayStart) {
      yesterday.push(c);
    } else {
      earlier.push(c);
    }
  }

  today.sort(byRecent);
  yesterday.sort(byRecent);
  earlier.sort(byRecent);

  return { today, yesterday, earlier };
}

function startOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}
